/* ─────────────────────────────────────────────────────────────────────────────
   D2C Freight Pricing — API reconciliation layer.

   The UI (built first, on mock data) and the backend (the 25 /api/d2c endpoints)
   use DIFFERENT vocabularies in a few places. This module is the single source of
   truth for translating between them, plus the registry of "gap notes" — precise,
   in-UI explanations wherever the UI offers something the API does not yet accept.

   RULE (per product owner): never remove UI; where the API can't take an input,
   wire what it accepts and surface a clear note stating the exact issue.
   ──────────────────────────────────────────────────────────────────────────── */

// ── Commercial card type: UI key → API cardType enum ─────────────────────────
// API cardType accepts BLENDED · COURIER_WISE · PRODUCT_SEGMENT. The backend
// (ProposalDocService.selectTemplate) picks the actual commercial format from
// cardType + selectedServices, so several UI cards share BLENDED:
//   blended → BLENDED                       (default → blended+penalty template)
//   cpwise  → COURIER_WISE                  (Enterprise vs SME auto-picked by lane count)
//   sddndd  → BLENDED + ONLY SDD_NDD service → SDD/NDD template
//   rvp     → BLENDED + RVP among services   → RVP-with-QC template
//   hb      → HB                             → H&B (Large) mini-agreement PDF (v2.1 §10)
export const CARD_TYPE_MAP = {
  blended: 'BLENDED',
  cpwise: 'COURIER_WISE',
  sddndd: 'BLENDED',
  rvp: 'BLENDED',
  hb: 'HB',
  standard: 'STANDARD',
}

// STANDARD-mode forward formats — the 5 pickable cards (RVP auto-attaches, never listed).
// key = FE id · api = cardFormat enum sent to POST /standard-card · label = UI text.
export const STANDARD_FORMATS = [
  { key: 'blended', label: 'Blended', api: 'BLENDED' },
  { key: 'cpwise', label: 'Courier-wise', api: 'COURIER_WISE' },
  { key: 'sdd', label: 'SDD', api: 'SDD' },
  { key: 'ndd', label: 'NDD', api: 'NDD' },
  { key: 'hnb', label: 'H&B', api: 'HNB' },
]

// rateCardMode for /generate & /counter (API: BLENDED | COURIER_WISE | PRODUCT_SEGMENT)
export const rateCardModeFor = (uiCardKey) => CARD_TYPE_MAP[uiCardKey] || 'BLENDED'

// ── Service type: UI key → API serviceType enum (clean, 1:1) ─────────────────
export const SERVICE_MAP = {
  srf: 'FWD_SURFACE',
  air: 'FWD_AIR',
  sddndd: 'SDD_NDD',
  rvp: 'RVP',
}
export const toApiServices = (uiKeys = []) => uiKeys.map((k) => serviceMeta(k).apiValue).filter(Boolean)

// ── Universal service resolver ───────────────────────────────────────────────
// A "service" in the wizard can now be one of three things, and this normalises all:
//   • a legacy UI key            — 'srf' | 'air' | 'sddndd' | 'rvp'
//   • a legacy API enum          — 'FWD_SURFACE' | 'FWD_AIR' | 'SDD_NDD' | 'RVP'
//   • a rich serviceability code — 'FWD_SURFACE_REG' | 'REV_REG_QC' | 'Intercity_NDD_LARGE' …
// `apiValue` is exactly what we send to the backend as serviceType (rich codes pass through
// untouched — the backend is being taught them; legacy keys still map to the 4 enums).
// isReverse / isForward / isAir / isSurface drive the per-service UI + payload branches so
// none of them hardcode the 4 any more. Old proposals keep working via the legacy tables.
const LEGACY_SVC = {
  srf: { label: 'FWD Surface', apiValue: 'FWD_SURFACE', kind: 'surface' },
  air: { label: 'FWD Air', apiValue: 'FWD_AIR', kind: 'air' },
  sddndd: { label: 'SDD & NDD', apiValue: 'SDD_NDD', kind: 'sddndd' },
  rvp: { label: 'Reverse (RVP)', apiValue: 'RVP', kind: 'reverse' },
}
const LEGACY_ENUM_TO_KEY = { FWD_SURFACE: 'srf', FWD_AIR: 'air', SDD_NDD: 'sddndd', RVP: 'rvp' }
const kindFlags = (kind) => ({
  kind, isReverse: kind === 'reverse', isAir: kind === 'air', isSurface: kind === 'surface',
  isSdd: kind === 'sddndd', isForward: kind === 'surface' || kind === 'air',
})
export const serviceMeta = (code, name) => {
  const c = String(code || '')
  if (LEGACY_SVC[c]) return { code: c, label: name || LEGACY_SVC[c].label, apiValue: LEGACY_SVC[c].apiValue, isLarge: false, ...kindFlags(LEGACY_SVC[c].kind) }
  if (LEGACY_ENUM_TO_KEY[c]) { const k = LEGACY_ENUM_TO_KEY[c]; return { code: c, label: name || LEGACY_SVC[k].label, apiValue: c, isLarge: false, ...kindFlags(LEGACY_SVC[k].kind) } }
  const U = c.toUpperCase()
  const kind = /(^|_)REV|RVP/.test(U) ? 'reverse' : /(SDD|NDD)/.test(U) ? 'sddndd' : /AIR/.test(U) ? 'air' : 'surface'
  // isLarge — the Large-parcel size variant (…_LARGE, 10–50 kg): Step 2's slab
  // builder starts these at a 5 kg first slab instead of the 0.5/1 kg parcel sizes.
  return { code: c, label: name || c, apiValue: c, isLarge: /LARGE/.test(U), ...kindFlags(kind) }
}
// Rich/legacy code → the 4-value D2C key (srf|air|sddndd|rvp) — for standard-card matching etc.
export const serviceCodeToKey = (code) => ({ surface: 'srf', air: 'air', reverse: 'rvp', sddndd: 'sddndd' }[serviceMeta(code).kind])

// ── Zone label: UI ↔ API. NOTE: UI constants say "Metro"; API expects "Metros". ─
export const ZONE_UI_TO_API = {
  Local: 'Local',
  'Within State': 'Within State',
  Metro: 'Metros',
  Metros: 'Metros',
  ROI: 'ROI',
  Special: 'Special',
}
export const ZONE_API_TO_UI = {
  Local: 'Local',
  'Within State': 'Within State',
  Metros: 'Metros',
  ROI: 'ROI',
  Special: 'Special',
}
export const toApiZone = (uiZone) => ZONE_UI_TO_API[uiZone] || uiZone

// ── inputMode (per service) ──────────────────────────────────────────────────
// UI offers 3 modes: margin · ratecard · standard. API accepts 2.
export const INPUT_MODE_MAP = {
  margin: 'TARGET_MARGIN',
  ratecard: 'TARGET_RATE_CARD',
  standard: null, // unresolved — see NOTES.input_standard
}

// ── Standard (GTM) rate cards ────────────────────────────────────────────────
// "Standard" mode hands the client a pre-built card from the Rate-Card Library
// as-is (no pricing math). Which card is picked = service × rate-card type
// (BLENDED/COURIER_WISE) × prospect tier (SME/ENTERPRISE).
//
// The backend RateCardLibraryItem is a fixed POJO (name·serviceType·description·
// fileUri) that silently drops unknown JSON fields — so, to stay FE-only (no
// backend model change), the two extra dimensions ride inside `description` as a
// trailing machine tag: "<human remark> ⟦rc:COURIER_WISE;tier:ENTERPRISE⟧".
// encode on write, parse+strip on read/display, match on lookup. Promoting these
// to real backend fields later is a drop-in change (parseCardMeta would just read
// item.rateCardType/prospectTier first).
const CARD_META_RE = /\s*⟦rc:([A-Z_]+);tier:([A-Z]+)⟧\s*$/

export const encodeCardMeta = (remark, rateCardType, prospectTier) => {
  const base = (remark || '').replace(CARD_META_RE, '').trim()
  if (!rateCardType && !prospectTier) return base
  return `${base}${base ? ' ' : ''}⟦rc:${rateCardType || 'BLENDED'};tier:${prospectTier || 'SME'}⟧`
}

// Reads the tag from description (or, forward-compatibly, from real fields if the
// backend ever adds them). Always returns { remark, rateCardType, prospectTier }.
export const parseCardMeta = (item = {}) => {
  const desc = item.description || ''
  const m = desc.match(CARD_META_RE)
  return {
    remark: desc.replace(CARD_META_RE, '').trim(),
    rateCardType: item.rateCardType || (m ? m[1] : null),
    prospectTier: item.prospectTier || (m ? m[2] : null),
  }
}

// Pick the best library card for a Standard-mode service. Exact match on all three
// dimensions first, then relax tier, then relax type, then service-only — so a
// partially-tagged library still resolves something and the UI can show what it
// matched (and how exact). Returns { card, exact } or { card: null } if nothing
// even shares the serviceType.
export const matchStandardCard = (library = [], { serviceType, rateCardType, prospectTier }) => {
  const pool = (Array.isArray(library) ? library : []).filter((c) => c && c.serviceType === serviceType)
  if (!pool.length) return { card: null, exact: false }
  const meta = (c) => parseCardMeta(c)
  const exact = pool.find((c) => meta(c).rateCardType === rateCardType && meta(c).prospectTier === prospectTier)
  if (exact) return { card: exact, exact: true }
  const byType = pool.find((c) => meta(c).rateCardType === rateCardType)
  const byTier = pool.find((c) => meta(c).prospectTier === prospectTier)
  return { card: byType || byTier || pool[0], exact: false }
}

// ── Dynamic Step-1 service picker (serviceability service_type_master → D2C enum) ──
// Step 1 offers only 3 categories (Forward · Reverse · SDD/NDD). Everything inside
// them collapses to exactly one of the 4 D2C service enums, so the value SENT to
// /proposals & /generate is unchanged — only the browsing UI is dynamic. High-Value
// and Hyperlocal are intentionally out (no D2C pricing target → would break generation).
export const STEP1_SERVICE_CATEGORIES = [
  { id: 'FORWARD', label: 'Forward' },
  { id: 'REVERSE', label: 'Reverse' },
  { id: 'SDD_NDD', label: 'SDD / NDD' },
]

// A serviceability service-type object → D2C service key (srf | air | sddndd | rvp),
// or null when it has no D2C pricing target. Surface (and any non-Air forward) → srf;
// Air → air; anything Reverse → rvp; anything SDD/NDD → sddndd. Reg vs Large, QC vs
// non-QC etc. all collapse to the same key — the sent enum is one of the 4 either way.
export const serviceTypeToKey = (st = {}) => {
  const cat = st.category
  const mode = String(st.mode || '').toUpperCase()
  if (cat === 'REVERSE' || st.direction === 'RVP') return 'rvp'
  if (cat === 'SDD_NDD') return 'sddndd'
  if (cat === 'FORWARD') return mode === 'AIR' ? 'air' : 'srf'
  return null // HIGH_VALUE / HYPERLOCAL
}

// ── Proposal status: API enum → display label + chip style key (STATUS_STYLES) ─
// UI mock used AWAITING_ORDER_DATA / CLOSED; the API enum differs. Always read
// status through this map so chips/filters never silently miss a state.
export const STATUS_META = {
  DRAFT: { label: 'Draft', style: 'draft' },
  GENERATED: { label: 'Generated', style: 'neutral' },
  PENDING_L1: { label: 'Pending at L1', style: 'pendingL1' },
  PUSHED_TO_PRICING_AWAITING_ORDER_DATA: { label: 'Awaiting Order-Level Data', style: 'awaiting' },
  PENDING_FH: { label: 'Pending with Functional Head', style: 'pendingFh' },
  RETURNED_TO_SL: { label: 'Returned to Sales Leader', style: 'returned' },
  RETURNED_TO_L1: { label: 'Returned to L1', style: 'returned' },
  APPROVED: { label: 'Approved', style: 'approved' },
  SENT: { label: 'Sent', style: 'shared' },
  CONVERTED: { label: 'Converted', style: 'converted' },
  LOST: { label: 'Lost', style: 'lost' },
}
export const statusMeta = (apiStatus) =>
  STATUS_META[apiStatus] || { label: apiStatus || '—', style: 'neutral' }

// ── Route number (API) → margin bucket key (UI BUCKETS) is NOT 1:1; the API
//    returns `bucket` directly ("Healthy" | "Strong" | ...). Map label → UI key. ─
export const BUCKET_LABEL_TO_KEY = {
  Healthy: 'HEALTHY',
  Strong: 'STRONG',
  Moderate: 'MODERATE',
  Borderline: 'BORDERLINE',
  'Borderline Profitable': 'BORDERLINE',
  'Loss Making': 'LOSS',
}

// ── Masking helper — SL responses null out cost/margin. Treat the API response
//    as the only source of truth; never derive a hidden value from a visible one. ─
export const isMasked = (lane) =>
  !lane || lane.costRate == null || lane.marginPct == null

// ── Reverse label maps (API enum → human label for tables/chips) ─────────────
export const SERVICE_API_TO_LABEL = {
  FWD_SURFACE: 'Surface',
  FWD_AIR: 'Air',
  SDD_NDD: 'SDD/NDD',
  RVP: 'RVP',
}
// Friendly label for ANY service value — the 4 base enums, a legacy UI key, or a rich
// serviceability code (FWD_SURFACE_LARGE, REV_REG_QC, Intercity_NDD_REG…). Built from the
// code structure (no live catalog needed, so it works in exports too). Falls back to the
// base kind for anything unrecognised.
export const serviceLabel = (code) => {
  const c = String(code || '')
  if (!c) return ''
  if (SERVICE_API_TO_LABEL[c]) return SERVICE_API_TO_LABEL[c]
  const U = c.toUpperCase()
  const kind = serviceMeta(c).kind
  let base
  if (kind === 'air') base = 'Air'
  else if (kind === 'reverse') base = 'RVP'
  else if (kind === 'sddndd') {
    if (/SC[-_ ]?NDD/.test(U)) base = 'Same-city NDD'
    else if (/INTERCITY|IC[-_ ]?NDD/.test(U)) base = 'Intercity NDD'
    else if (/SDD/.test(U) && !/NDD/.test(U)) base = 'SDD'
    else if (/NDD/.test(U) && !/SDD/.test(U)) base = 'NDD'
    else base = 'SDD/NDD'
  } else base = 'Surface'
  const tags = []
  if (/LARGE/.test(U)) tags.push('Large')
  else if (/(^|_)REG(_|$)/.test(U)) tags.push('Regular')
  if (/NO[-_]?QC/.test(U)) tags.push('non-QC')
  else if (/QC/.test(U)) tags.push('QC')
  return tags.length ? `${base} · ${tags.join(', ')}` : base
}
export const CARD_TYPE_API_TO_LABEL = {
  BLENDED: 'Blended',
  COURIER_WISE: 'Courier-wise',
  PRODUCT_SEGMENT: 'Product segment',
}
// "Blended · Surface + Air + RVP"
export const cardLabel = (cardType, selectedServices = []) => {
  const base = CARD_TYPE_API_TO_LABEL[cardType] || cardType || '—'
  const svcs = (selectedServices || []).map((s) => serviceLabel(s)).join(' + ')
  return svcs ? `${base} · ${svcs}` : base
}

// ── Relative "waiting" time from an ISO timestamp ────────────────────────────
export const relativeTime = (iso) => {
  if (!iso) return '—'
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  const mins = Math.max(0, Math.round((Date.now() - then) / 60000))
  if (mins < 60) return `${mins} min`
  const hrs = mins / 60
  if (hrs < 24) return `${hrs.toFixed(1)} hrs`
  return `${Math.round(hrs / 24)} d`
}

// "Updated" cells show date AND time (feedback #14) — e.g. "02/07/2026, 14:35".
export const fmtDateTime = (iso) => {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

// Who currently holds the proposal, derived from status (list payload carries no
// reviewer NAME — only reviewerIds — so we show the role; name needs a separate lookup).
export const holderFromStatus = (apiStatus) => {
  switch (apiStatus) {
    case 'PENDING_L1':
    case 'RETURNED_TO_L1':
      return 'L1 Pricing Reviewer'
    case 'PENDING_FH':
      return 'Functional Head'
    case 'PUSHED_TO_PRICING_AWAITING_ORDER_DATA':
      return 'Pricing (awaiting order data)'
    case 'RETURNED_TO_SL':
      return 'Sales Leader'
    default:
      return '—'
  }
}

// Short outcome label + chip style from status (for dashboards/history).
export const outcomeLabel = (apiStatus, lossReason) => {
  switch (apiStatus) {
    case 'CONVERTED': return 'Converted'
    case 'LOST': return lossReason ? `Lost · ${lossReason}` : 'Lost'
    case 'SENT': return 'Shared'
    case 'APPROVED': return 'Approved'
    case 'DRAFT': return 'Draft'
    case 'RETURNED_TO_SL': return 'Returned to you'
    default: return 'Awaiting approval'
  }
}
export const outcomeStyle = (apiStatus) => {
  switch (apiStatus) {
    case 'CONVERTED': return 'converted'
    case 'LOST': return 'lost'
    case 'SENT':
    case 'APPROVED': return 'approved'
    case 'RETURNED_TO_SL': return 'returned'
    default: return 'neutral'
  }
}

// Coarse "why it's here" derived from status (exact exception detail —
// guardrail_breach etc. — lives on the version's capturedInputs, not the list).
// route/dealBucket/orderDataFileUri enrich the reason with WHY L1 must act (Decision Flow
// v7 Stage 2 / mockup queue: "L1 approval mandatory (10–20%)", "Order-level RFI attached —
// L1 rebuilds & submits to FH") instead of a flat status label. Courier-guardrail-driven
// reasons ("L1 approval + <courier> override") aren't buildable yet — no guardrail-breach
// field is exposed on the list API (tracked with the EDG-02 engine-enforcement gap).
export const reasonFromStatus = (apiStatus, route, dealBucket, orderDataFileUri) => {
  switch (apiStatus) {
    case 'PENDING_L1': {
      const band = route === 1 ? '>20%' : route === 2 ? '10–20%' : dealBucket ? dealBucket : null
      return {
        label: band ? `L1 approval mandatory (${band})` : 'L1 approval mandatory',
        key: 'pendingL1',
      }
    }
    case 'RETURNED_TO_L1': return { label: 'Returned by FH', key: 'returned' }
    case 'PENDING_FH': return { label: 'Escalated / pricing-authored', key: 'pendingFh' }
    case 'PUSHED_TO_PRICING_AWAITING_ORDER_DATA':
      return orderDataFileUri
        ? { label: 'Order-level data attached — rebuild in progress', key: 'awaiting' }
        : { label: 'Order-level RFI sent — awaiting data from brand', key: 'awaiting' }
    default: return { label: statusMeta(apiStatus).label, key: statusMeta(apiStatus).style }
  }
}

// What the reviewer did, inferred from the resulting status (best-effort — the
// authoritative action is in the version approvalChain / proposal events).
export const reviewerActionFor = (apiStatus) => {
  switch (apiStatus) {
    case 'APPROVED':
    case 'SENT':
    case 'CONVERTED': return { label: 'Approved', key: 'approved' }
    case 'LOST': return { label: 'Declined', key: 'lost' }
    case 'RETURNED_TO_SL':
    case 'RETURNED_TO_L1': return { label: 'Returned', key: 'returned' }
    case 'PENDING_FH': return { label: 'Escalated / countered', key: 'pendingFh' }
    default: return { label: statusMeta(apiStatus).label, key: statusMeta(apiStatus).style }
  }
}

// Pending approver role ("L1" | "FH" | null) → display label. Falls back to a
// status-derived holder when the list doesn't carry pendingApproverRole.
export const approverLabel = (pendingApproverRole, apiStatus) => {
  if (pendingApproverRole === 'L1') return 'L1 Pricing Reviewer'
  if (pendingApproverRole === 'FH') return 'Functional Head'
  return holderFromStatus(apiStatus)
}

// ── ProposalListItem (v1.2 enriched, or legacy D2cProposal) → table row ──────
// v1.2 GET /proposals returns dealId + version data (route, dealBucket,
// dealMarginPct, pendingApproverRole, orderDataFileUri). We read those directly;
// fields fall back gracefully for the legacy shape (id, no version data).
export const proposalRow = (p = {}) => {
  const meta = statusMeta(p.status)
  const card = cardLabel(p.cardType, p.selectedServices)
  const id = p.dealId || p.id
  return {
    id,
    proposalId: id,
    versionId: p.currentVersionId || null,
    brand: p.brandName || '—',
    hubspot: p.hubspotDealId || '—',
    card,
    cardType: card, // alias for screens whose column key is 'cardType'
    status: meta.label,
    statusKey: meta.style,
    apiStatus: p.status,
    route: p.route ?? null,
    with: approverLabel(p.pendingApproverRole, p.status),
    waiting: relativeTime(p.updatedAt),
    tat: relativeTime(p.updatedAt), // dashboards show turnaround time here, not transit TAT
    // Feedback #14: "Updated" must show the time of last update, not just the date.
    sent: fmtDateTime(p.updatedAt),
    closed: fmtDateTime(p.updatedAt),
    outcome: outcomeLabel(p.status, p.lossReason),
    outcomeKey: outcomeStyle(p.status),
    // Margin band — now provided on the list (dealBucket); SL margin still masked (null).
    band: p.dealBucket || '—',
    bandKey: p.dealBucket ? BUCKET_LABEL_TO_KEY[p.dealBucket] : null,
    // RETURNED_TO_SL is actionable: the SL revises inputs and regenerates (T18 reroute).
    action: ['DRAFT', 'GENERATED'].includes(p.status) ? 'Resume draft'
      : p.status === 'RETURNED_TO_SL' ? 'Revise & resubmit' : 'View',
    resumable: ['DRAFT', 'GENERATED', 'RETURNED_TO_SL'].includes(p.status),
    // L1/FH columns. Owner name resolved server-side (BUG-09: ownedByName); id only as fallback.
    sl: p.ownedByName || p.ownedBy || '—',
    from: approverLabel(p.pendingApproverRole, p.status),
    reason: reasonFromStatus(p.status, p.route, p.dealBucket, p.orderDataFileUri).label,
    why: reasonFromStatus(p.status, p.route, p.dealBucket, p.orderDataFileUri).label,
    whyKey: reasonFromStatus(p.status, p.route, p.dealBucket, p.orderDataFileUri).key,
    whyDetail: null,
    margin: p.dealMarginPct != null ? `${p.dealMarginPct}%` : '—', // null = masked (SL)
    orderDataFileUri: p.orderDataFileUri || null,
    reviewerAction: reviewerActionFor(p.status).label,
    reviewerActionKey: reviewerActionFor(p.status).key,
    decision: reviewerActionFor(p.status).label,
    decisionKey: reviewerActionFor(p.status).key,
    lossReason: p.lossReason || null,
    raw: p,
  }
}

// Status groupings used to split the single GET /proposals list across the
// "awaiting decision" vs "recently sent/closed" tables.
export const STATUS_GROUPS = {
  awaiting: ['PENDING_L1', 'PENDING_FH', 'PUSHED_TO_PRICING_AWAITING_ORDER_DATA', 'RETURNED_TO_L1'],
  returnedToSl: ['RETURNED_TO_SL'],
  // "Recently sent" = anything the SL has sent onward (left DRAFT/GENERATED) — not just
  // client-shared deals, so the table has data as soon as anything is submitted.
  recent: ['PENDING_L1', 'PENDING_FH', 'PUSHED_TO_PRICING_AWAITING_ORDER_DATA', 'RETURNED_TO_SL', 'RETURNED_TO_L1', 'APPROVED', 'SENT', 'CONVERTED', 'LOST'],
  open: ['DRAFT', 'GENERATED', 'PENDING_L1', 'PENDING_FH', 'PUSHED_TO_PRICING_AWAITING_ORDER_DATA', 'RETURNED_TO_SL', 'RETURNED_TO_L1'],
  // L1 acts on these; FH acts on PENDING_FH. Pushed-awaiting-order-data deals are
  // visible to reviewers read-only (state-machine reference §5 — visibility is
  // status-independent via reviewerIds; the review screen shows a read-only banner).
  l1Queue: ['PENDING_L1', 'RETURNED_TO_L1', 'PUSHED_TO_PRICING_AWAITING_ORDER_DATA'],
  l1History: ['PENDING_FH', 'APPROVED', 'SENT', 'CONVERTED', 'LOST', 'RETURNED_TO_SL'],
  fhQueue: ['PENDING_FH', 'PUSHED_TO_PRICING_AWAITING_ORDER_DATA'],
  closed: ['APPROVED', 'SENT', 'CONVERTED', 'LOST'],
}
export const inGroup = (group, apiStatus) => (STATUS_GROUPS[group] || []).includes(apiStatus)

// ── Version detail adapters (GET /proposal-versions/{id}) ────────────────────
const ZONE_ORDER = ['Local', 'Within State', 'Metros', 'ROI', 'Special']

// A lane's ACTUAL monthly shipment count — the summable per-lane share (courier
// allocation + payment-mode split). This is the correct weight for a shipment-weighted
// margin blend. volumeShipments must NOT be used as the weight: it's the full zone×slab
// total repeated identically on every lane in the cell, so weighting by it makes every
// lane equal and collapses the blend to a plain average. Falls back to
// volumeShipments×allocationPct, then volumeShipments, then 0 when no shipment data exists.
export const laneShipments = (l = {}) =>
  l.allocatedShipments != null ? l.allocatedShipments
    : (l.volumeShipments != null && l.allocationPct != null) ? l.volumeShipments * (l.allocationPct / 100)
      : (l.volumeShipments != null ? l.volumeShipments : 0)

// maskVersionMargins — frontend defence-in-depth for Sales-Leader viewers: null out
// every margin/cost field on a version before rendering, so even if the backend's
// role-masking ever slips (e.g. on APPROVED versions), the UI still shows "masked".
// NOTE: this is a display guard only — real protection is the backend masking; the
// unmasked numbers would still be in the network response for anyone with DevTools.
export const maskVersionMargins = (version) => (version ? {
  ...version,
  dealMarginPct: null,
  services: (version.services || []).map((s) => ({ ...s, marginPct: null })),
  computedLanes: (version.computedLanes || []).map((l) => ({ ...l, marginPct: null, costRate: null })),
  computedRtoLanes: (version.computedRtoLanes || []).map((l) => ({ ...l, marginPct: null, costRate: null })),
} : version)

// computedLanes[] → heat-map { zones, rows:[{wt, cells:[{v,cls}]}] }.
// Cell = shipment-weighted avg marginPct across lanes in that (weightSlab × zone),
// weighted by each lane's actual allocated shipments (laneShipments), NOT the repeated
// volumeShipments. For SL (masked) marginPct is null → cell shows "—". Cls by thresholds.
export const lanesToHeatmap = (lanes = []) => {
  const zones = ZONE_ORDER.filter((z) => lanes.some((l) => l.zone === z))
  const extraZones = [...new Set(lanes.map((l) => l.zone))].filter((z) => !ZONE_ORDER.includes(z))
  const allZones = [...zones, ...extraZones]
  // Slabs sort by their numeric kg value — with arbitrary slab labels now allowed
  // ("4 Kg", "7 Kg"…), the old canonical-list-first ordering put 10 Kg before 4 Kg.
  const allSlabs = [...new Set(lanes.map((l) => l.weightSlab))].filter(Boolean)
    .sort((a, b) => (parseFloat(a) || 0) - (parseFloat(b) || 0))

  const cls = (m) => (m == null ? 'y' : m >= 15 ? 'g2' : m >= 10 ? 'g' : m >= 0 ? 'y' : 'r')
  const rows = allSlabs.map((wt) => ({
    wt,
    cells: allZones.map((z) => {
      const cell = lanes.filter((l) => l.weightSlab === wt && l.zone === z)
      if (!cell.length) return { v: '—', cls: 'y' }
      const withM = cell.filter((l) => l.marginPct != null)
      if (!withM.length) return { v: '—', cls: 'y' } // masked
      const totVol = withM.reduce((s, l) => s + laneShipments(l), 0)
      // shipment-weighted average; fall back to a plain mean only if no lane carries shipment data
      const m = totVol > 0
        ? withM.reduce((s, l) => s + l.marginPct * laneShipments(l), 0) / totVol
        : withM.reduce((s, l) => s + l.marginPct, 0) / withM.length
      return { v: `${m.toFixed(1)}%`, cls: cls(m) }
    }),
  }))
  return { zones: allZones, rows }
}

// versions[] (GET /proposals/{id}/versions) → thread table rows. "What changed" is
// computed client-side — margin/route delta vs the previous seq. The API has no
// change-summary field, and a column that always read "—" was worse than none.
export const versionThreadRows = (versions = [], currentVersionId) => {
  const bySeq = [...versions].sort((a, b) => (a.seq || 0) - (b.seq || 0))
  const changeById = {}
  bySeq.forEach((v, i) => {
    if (i === 0) { changeById[v.id] = 'First version'; return }
    const prev = bySeq[i - 1]
    const parts = []
    if (v.dealMarginPct != null && prev.dealMarginPct != null) {
      const d = Math.round((v.dealMarginPct - prev.dealMarginPct) * 10) / 10
      parts.push(d === 0 ? 'Margin unchanged' : `Margin ${d > 0 ? '+' : ''}${d} pt`)
    }
    if (v.route != null && prev.route != null && v.route !== prev.route) parts.push(`Route ${prev.route} → ${v.route}`)
    changeById[v.id] = parts.length ? parts.join(' · ') : '—'
  })
  return versions.map((v) => ({
    v: `v${v.seq}${v.id === currentVersionId ? ' · current' : ''}`,
    by: v.authorRole || '—',
    change: changeById[v.id] || '—',
    margin: v.dealMarginPct == null ? 'masked' : `${v.dealMarginPct}%`,
    route: v.route != null ? `Route ${v.route}` : '—',
    reached: v.dealBucket || '—',
    reachedKey: BUCKET_LABEL_TO_KEY[v.dealBucket] ? 'neutral' : 'neutral',
    when: v.createdAt ? new Date(v.createdAt).toLocaleString('en-GB') : '—',
    current: v.id === currentVersionId,
    id: v.id,
  }))
}

// version.approvalChain[] → audit rows (per-version decision log).
export const approvalChainRows = (version = {}) =>
  (version.approvalChain || []).map((s, i) => ({
    when: s.decidedAt ? new Date(s.decidedAt).toLocaleString('en-GB') : (s.assignedAt ? new Date(s.assignedAt).toLocaleString('en-GB') : '—'),
    who: s.userName || s.level || '—',
    action: `${s.status}${s.remarks ? ` — ${s.remarks}` : ''}`,
    _k: `${s.level}-${i}`,
  }))

// ── Wizard → API payload builders ────────────────────────────────────────────
// Weight band {from,to} → API weightSlab label, keyed on the band's upper bound.
// Confirmed prod bug (deal ABC, TARGET_MARGIN, Aug 2026): this used to snap every
// boundary to the nearest of a fixed 6-checkpoint list (0.5/1/2/3/5/10 Kg), then the
// RFI-entries merge below summed % across whatever bands landed on the same checkpoint
// — an 11-band ladder silently collapsed to 6 slabs before it ever reached storage.
// Backend now range-matches weightSlab by its numeric value directly (confirmed for
// arbitrary sub-10 kg labels) rather than requiring one of that fixed enum, so this
// just formats the boundary as entered — no snapping, no merging.
export const bandToSlab = (to) => `${Math.round((parseFloat(to) || 0) * 100) / 100} Kg`

// D2C wizard redesign (Aug 2026) — the weight-slab structure (free-form: pick any slab
// size + count per band) is now built in Step 2 instead of per-service in Step 3, and
// carries forward "as is" — Step 3 only fills in ₹ rates against the rows it produces.
//
// tierSlabRanges: one {from,to} PER INDIVIDUAL SLAB (not per band) — e.g. tiers
// [{size:0.5,count:2},{size:1,count:3}] → [{from:0,to:0.5},{from:0.5,to:1},{from:1,to:2},
// {from:2,to:3},{from:3,to:4}]. There's no "band" concept on the wire or in the RFI/rate
// card grids — every row is its own weight slab. This is what Step 2's weight-wise
// %-split rows use — one % per slab, same granularity as Step 3's rate-card grid below.
export const tierSlabRanges = (tiers = []) => {
  const rows = []
  let cur = 0
  ;(tiers || []).forEach((t) => {
    const size = Number(t.size) || 0
    const count = Math.max(0, Math.floor(Number(t.count) || 0))
    if (!size || !count) return
    for (let i = 0; i < count; i++) {
      const from = cur
      const to = Math.round((cur + size) * 100) / 100
      rows.push({ from, to })
      cur = to
    }
  })
  return rows
}

// rateRowLabelsFromTiers: same walk as tierSlabRanges, as literal "from–to kg" string
// labels instead of {from,to} numbers. This is what Step 3's Target Rate Card grid uses
// — pricing granularity per slab, same as a real client rate card.
export const rateRowLabelsFromTiers = (tiers = []) => {
  const rows = []
  let cur = 0
  ;(tiers || []).forEach((t) => {
    const size = Number(t.size) || 0
    const count = Math.max(0, Math.floor(Number(t.count) || 0))
    if (!size || !count) return
    for (let i = 0; i < count; i++) {
      const from = cur
      const to = Math.round((cur + size) * 100) / 100
      rows.push(`${from}–${to} kg`)
      cur = to
    }
  })
  return rows
}

// compactRateRows: describes the compact "base + increment" entry rows for a tier
// ladder — one row per FIELD instead of one row per individual slab. Band 0 (the
// first slab) always gets a flat FIRST rate; if it covers more than one slab, a
// FIRST-EACH row covers the rest. Every band after that gets an EACH row (the
// increment per slab in that band), plus a BASE row only when the band is
// "fresh" (its own quoted starting number, not built up from the band before it).
export const compactRateRows = (tiers = []) => {
  const rows = []
  ;(tiers || []).forEach((t, i) => {
    const count = Math.max(0, Math.floor(Number(t.count) || 0))
    if (!count) return
    if (i === 0) {
      rows.push({ bandIndex: i, kind: 'first' })
      if (count > 1) rows.push({ bandIndex: i, kind: 'firstEach' })
    } else {
      if (t.fresh !== false) rows.push({ bandIndex: i, kind: 'base' })
      rows.push({ bandIndex: i, kind: 'each' })
    }
  })
  return rows
}

// expandCompactRates: turns the compact per-band rate inputs into the SAME full
// slab × zone rate table rateRowLabelsFromTiers/cfg.rates already use — same
// "from–to kg" row-label keys, same shape — so nothing downstream (the generate
// payload, the validator) needs to know the compact grid exists.
//
// Math (matches the reference rate-card-v2-dynamic.html prototype):
//   band 0, slab 1        = compact.first[zone]                              (quoted directly)
//   band 0, slab k (k>1)  = compact.first[zone] + compact.firstEach[zone] * (k-1)
//   band i>0, slab k      = anchor + compact.bands[i].each[zone] * k, where
//     anchor = compact.bands[i].base[zone]   if the band is "fresh"           (quoted directly)
//            = running total at the end of the previous band, otherwise      (continues)
// A band's own "base" number is a reference point at the START of the band, not
// itself an output slab — the band's first actual slab is base + each × 1.
// One slab's row, given its per-zone anchor + per-zone increment and slab number k
// (1-indexed within its band). Pulled out of expandCompactRates so the per-tier loop
// below never defines a closure inside a loop body (no-loop-func).
const compactSlabRow = (zones, anchor, each, k) => {
  const row = {}
  zones.forEach((z) => {
    const a = anchor[z]
    const e = Number(each?.[z])
    row[z] = (a != null && Number.isFinite(e)) ? Math.round(a + e * k) : ''
  })
  return row
}

export const expandCompactRates = (tiers = [], compact = {}, zones = []) => {
  const rates = {}
  let running = {}
  let cur = 0
  ;(tiers || []).forEach((t, i) => {
    const size = Number(t.size) || 0
    const count = Math.max(0, Math.floor(Number(t.count) || 0))
    if (!size || !count) return
    if (i === 0) {
      const firstVal = {}
      zones.forEach((z) => {
        const v = Number(compact.first?.[z])
        firstVal[z] = Number.isFinite(v) ? v : null
      })
      const to0 = Math.round((cur + size) * 100) / 100
      const firstRow = {}
      zones.forEach((z) => { firstRow[z] = firstVal[z] == null ? '' : Math.round(firstVal[z]) })
      rates[`${cur}–${to0} kg`] = firstRow
      let stepCur = to0
      for (let k = 2; k <= count; k++) {
        const to = Math.round((stepCur + size) * 100) / 100
        rates[`${stepCur}–${to} kg`] = compactSlabRow(zones, firstVal, compact.firstEach, k - 1)
        stepCur = to
      }
      const nextRunning = {}
      zones.forEach((z) => {
        const each = Number(compact.firstEach?.[z])
        nextRunning[z] = firstVal[z] == null ? null : firstVal[z] + (count > 1 && Number.isFinite(each) ? each * (count - 1) : 0)
      })
      running = nextRunning
      cur = stepCur
    } else {
      const bandCfg = compact.bands?.[i] || {}
      const fresh = t.fresh !== false
      const anchor = {}
      zones.forEach((z) => {
        const v = Number(bandCfg.base?.[z])
        anchor[z] = fresh ? (Number.isFinite(v) ? v : null) : (running[z] ?? null)
      })
      let stepCur = cur
      for (let k = 1; k <= count; k++) {
        const to = Math.round((stepCur + size) * 100) / 100
        rates[`${stepCur}–${to} kg`] = compactSlabRow(zones, anchor, bandCfg.each, k)
        stepCur = to
      }
      const nextRunning = {}
      zones.forEach((z) => {
        const each = Number(bandCfg.each?.[z])
        const a = anchor[z]
        nextRunning[z] = (a != null && Number.isFinite(each)) ? a + each * count : (running[z] ?? null)
      })
      running = nextRunning
      cur = stepCur
    }
  })
  return rates
}

// ── Rate-card recipe v2 (D2C_RATE_CARD_RECIPE_FE_SPEC_V2.md) ─────────────────
// The compact "base + increment" description of each service's rate card, sent in
// capturedInputs so the client PDF can print the collapsed SME-commercials form and
// the wizard can restore Step 2/3 on resume. The backend re-derives the expansion
// and reconciles it against the priced lanes before trusting it — a recipe that
// doesn't reproduce the lanes just falls back to the full table, never a wrong card.
const kgTxt = (n) => `${Math.round(Number(n) * 100) / 100} Kg`
// Labels in the reference-PDF vocabulary (spec §4): "1 Kg" / "Addn 1 Kg" /
// "Base Rate at 3 Kg". The fresh-anchor phrase is non-negotiable: the anchor is the
// band's arithmetic starting point, NOT the chargeable rate at that weight — a bare
// "3 Kg | 100" row on a client commercial would state a price the deal doesn't charge.
const recipeLabels = (tiers) => {
  const labels = { firstSlab: kgTxt(tiers[0].size), bands: {} }
  if (tiers[0].count > 1) labels.firstEach = `Addn ${kgTxt(tiers[0].size)}`
  let from = tiers[0].size * tiers[0].count
  tiers.slice(1).forEach((t, j) => {
    const i = j + 1
    const b = { each: `Addn ${kgTxt(t.size)}` }
    if (t.fresh !== false) b.base = `Base Rate at ${kgTxt(from)}`
    labels.bands[String(i)] = b
    from = Math.round((from + t.size * t.count) * 100) / 100
  })
  return labels
}
// {uiZone: "50"} → {apiZone: 50}: zone keys through ZONE_UI_TO_API (spec §2 rule 1),
// values as JSON numbers (rule in §6.6); empty/invalid cells are omitted.
const apiZoneNums = (obj = {}) => {
  const out = {}
  Object.entries(obj || {}).forEach(([z, v]) => {
    const n = Number(v)
    if (String(v ?? '').trim() !== '' && Number.isFinite(n)) out[toApiZone(z)] = n
  })
  return Object.keys(out).length ? out : null
}
const apiCompact = (c = {}, tiers = []) => {
  const bands = {}
  tiers.forEach((t, i) => {
    if (i === 0) return
    const b = c.bands?.[i] || {}
    const entry = { each: apiZoneNums(b.each) }
    if (t.fresh !== false) entry.base = apiZoneNums(b.base)
    bands[String(i)] = entry
  })
  return { first: apiZoneNums(c.first), firstEach: tiers[0]?.count > 1 ? apiZoneNums(c.firstEach) : null, bands }
}
export const buildRateCardRecipe = ({ services = [], svcConfig = {}, wtTiers = [], svcSplits = null, courierWise = false }) => {
  const recipe = {}
  services.forEach((k) => {
    const cfg = svcConfig[k] || {}
    const meta = serviceMeta(k)
    const st = meta.apiValue
    if (!st) return
    const tiers = (svcSplits?.[k]?.tiers || wtTiers || [])
      .map((t) => ({ size: Number(t.size) || 0, count: Math.max(0, Math.floor(Number(t.count) || 0)), fresh: t.fresh !== false }))
      .filter((t) => t.size > 0 && t.count > 0)
    if (!tiers.length) return
    const entry = {
      structure: tiers.map((t, i) => (i === 0 ? { size: t.size, count: t.count } : { size: t.size, count: t.count, fresh: t.fresh })),
      labels: recipeLabels(tiers),
    }
    // TARGET_MARGIN services deliberately send structure + labels only (spec §2 rule 5):
    // engine-solved rates don't follow base+increment arithmetic, so there's nothing to
    // collapse — but the structure still powers resume.
    const isLinkedRvp = meta.isReverse && cfg.rvpMode === 'linked'
    if (!isLinkedRvp && cfg.mode === 'ratecard') {
      if (courierWise) {
        // COURIER_WISE: one block per courier PARENT (spec §2 rule 2), keys exactly as
        // they appear on lanes' courierParent — cfg.primary already stores parent names.
        const cr = {}
        ;(cfg.primary || []).forEach(({ parent }) => {
          if (!parent || !cfg.courierCompactRates?.[parent]) return
          cr[parent] = apiCompact(cfg.courierCompactRates[parent], tiers)
        })
        if (Object.keys(cr).length) entry.courierRates = cr
      } else if (cfg.compactRates) {
        entry.compactRates = apiCompact(cfg.compactRates, tiers)
      }
    }
    recipe[st] = entry
  })
  return Object.keys(recipe).length ? recipe : undefined
}

// POST /proposals body (Step 1). cardType falls back to BLENDED for unmapped UI cards.
export const buildCreatePayload = ({ hubspot, brand, cardType, services, revPot }) => ({
  hubspotDealId: hubspot || undefined,
  brandName: brand,
  cardType: CARD_TYPE_MAP[cardType] || 'BLENDED',
  selectedServices: toApiServices(services),
  monthlyRevenuePotentialLacs: revPot ? Number(revPot) : undefined,
})

// POST /generate body (Step 3). Returns { payload, warnings:[NOTE ids] } — warnings
// list every field the wizard cannot currently supply so the UI can show notes.
// wtTiers = the deal-wide slab structure built in Step 2 ("Split differs by service
// type" OFF); svcSplits[k].tiers, when present, is that service's own structure (toggle
// ON) and wins.
// registeredCompany/enterpriseSize/brandTotalVol/minWt/currentRvp — Freight-commercials
// scoping fields (Step 1, d2c-scoping-v8.html) with no dedicated backend field yet. Ride
// in capturedInputs (Map<String,Object>, schemaless — no backend change needed) the same
// way courierBlacklist/serviceabilityGoAheads/standardCards already do. NOTE: these can
// only go out via /generate — the earlier POST /proposals (buildCreatePayload) has no
// capturedInputs slot on its request DTO and no D2cProposalVersion exists yet at that
// point, so anything extra sent there would be silently dropped.
export const buildGeneratePayload = ({ cardType, services, svcConfig = {}, pickups = [], zoneSplit = [], wtSplit = [], wtTiers = [], svcSplits = null, totVol, codShare, rtoShare, avgInvoiceValue, pinCache = {}, registeredCompany, enterpriseSize, brandTotalVol, minWt, currentRvp }) => {
  const warnings = new Set()
  if (!CARD_TYPE_MAP[cardType]) warnings.add(`card_${cardType}`)

  const UI_ZONES = ['Local', 'Within State', 'Metro', 'ROI', 'Special'] // Step-3 grid columns
  const apiServices = services.map((k) => {
    const cfg = svcConfig[k] || {}
    const meta = serviceMeta(k)
    const serviceType = meta.apiValue   // legacy key → 4 enum; rich code → itself (backend is taught it)
    const isReverse = meta.isReverse
    const isFwd = meta.isForward
    const inputMode = INPUT_MODE_MAP[cfg.mode] || 'TARGET_MARGIN'
    if (cfg.mode === 'standard') warnings.add('input_standard')

    // cities: new shape = [{value, vol}]; tolerate legacy comma string.
    const cities = Array.isArray(cfg.cities)
      ? cfg.cities.map((c) => (c.value || '').trim()).filter(Boolean)
      : (typeof cfg.cities === 'string' ? cfg.cities.split(',').map((t) => t.trim()).filter(Boolean) : [])
    // D2C Courier Selection Pivot (D2C_PRICING_PIVOT_FE_INTEGRATION.md §2) — cpPreference
    // now carries PARENT NAMES (e.g. "Shadowfax"), not account codes; the backend resolves
    // the cheapest eligible account per lane. cfg.primary entries are { parent } only.
    const cpPreference = (cfg.primary || []).map((c) => c.parent).filter(Boolean)

    const svc = {
      serviceType,
      monthlyVolume: Number(cfg.vol) || 0,
      citiesInScope: cities.length ? cities : undefined,
      cpPreference: cpPreference.length ? cpPreference : undefined,
      inputMode,
    }
    if (inputMode === 'TARGET_MARGIN') svc.targetMarginPct = cfg.margin ? Number(cfg.margin) : undefined
    if (inputMode === 'TARGET_RATE_CARD') {
      // Option B (approved Aug 2026, closes the same prod bug as bandToSlab above): emit
      // one row per the SL's OWN real slab, not sampled at a fixed 6-checkpoint enum. The
      // old version keyed rows by DISCRETE_SLABS (0.5/1/2/3/5/10 Kg only) via
      // slabRateFromLadder, which silently discarded every intermediate slab the SL built
      // (e.g. a 4/6/7/8/9 kg structure was never sent at all). weightSlab is each real
      // slab's own upper edge — the engine range-matches on that number directly.
      // D2C wizard redesign (Aug 2026) — the ladder is built once in Step 2 (wtTiers), or per
      // service when "Split differs by service type" is on (svcSplits[k].tiers wins then).
      const tiers = svcSplits?.[k]?.tiers || wtTiers
      const ranges = tierSlabRanges(tiers)
      const labels = rateRowLabelsFromTiers(tiers)
      const rowsFor = (effRates) => ranges.map((rng, i) => ({
        weightSlab: bandToSlab(rng.to),
        zoneRates: UI_ZONES.reduce((acc, z) => {
          const v = effRates?.[labels[i]]?.[z]
          const r = (v == null || String(v).trim() === '') ? null : Math.round(Number(v))
          if (r != null && r > 0) acc[toApiZone(z)] = r
          return acc
        }, {}),
      })).filter((row) => Object.keys(row.zoneRates).length)
      // Courier-wise deals capture rates PER COURIER (cfg.courierCompactRates), so the engine gets
      // ONE CARD PER PREFERRED COURIER, keyed by courier brand — the engine matches a card to an
      // account by account code OR brand (D2cPricingEngine.rateFromCard). Previously a single
      // courier-less card carried only the first courier's rates, and the engine applied a
      // courier-less card to EVERY courier: courier #2+ were priced, and quoted on the PDF, at
      // courier #1's rates (Oct 2026). Re-derived from the compact grids on every build, so a courier
      // re-order after typing can never leave stale rows on the wire. Blended keeps one card.
      const cwCards = rateCardModeFor(cardType) === 'COURIER_WISE'
        ? (cfg.primary || []).map(({ parent }) => {
          const compact = parent ? cfg.courierCompactRates?.[parent] : null
          return compact ? { courierId: parent, rows: rowsFor(expandCompactRates(tiers, compact, UI_ZONES)) } : null
        }).filter((c) => c && c.rows.length)
        : []
      // No per-courier grid at all (a deal from before courier-wise capture) → the shared grid as
      // one courier-less card, exactly as before.
      svc.targetRateCards = cwCards.length ? cwCards : [{ courierId: null, rows: rowsFor(cfg.rates) }]
      if (!svc.targetRateCards.some((c) => c.rows.length)) warnings.add('gen_target_ratecard')
    }
    // Forward COD/RTO — now captured in Step 3.
    if (isFwd) {
      const cod = cfg.cod || {}
      // Blank-checks, not truthiness: an entered 0 (COD / RTO not charged) must reach the engine
      // as 0 — a dropped field falls back to the engine's default rails.
      const entered = (v) => String(v ?? '').trim() !== ''
      if (entered(cod.flat)) svc.codFlatInr = Number(cod.flat)
      if (cod.pct) svc.codPctOfValue = Number(cod.pct)
      if (entered(cod.rtoPct)) svc.rtoPctOfForward = Number(cod.rtoPct)
      if (!cod.flat && !cod.pct && !cod.rtoPct) warnings.add('gen_fwd_cod')
      // codShipmentPct/rtoShipmentPct (what FRACTION of shipments are COD / RTO — distinct
      // from cod.pct above, which is COD's % OF ORDER VALUE) have no per-service Step-3
      // field; only Step 1's deal-level codShare/rtoShare exist. Apply that single value
      // to every forward service until a per-service input exists.
      if (codShare) svc.codShipmentPct = Number(codShare)
      if (rtoShare) {
        svc.rtoShipmentPct = Number(rtoShare)
        // prepaidRtoShipmentPct/codRtoShipmentPct (D2C RTO Margin Breakout, §4) — no longer a
        // per-service Step-3 input. Both fields get the SAME value: Step 1's deal-level
        // Current RTO %, applied uniformly to every forward service (product decision — not
        // split by COD share; the RTO rate is treated as uniform across payment modes).
        svc.codRtoShipmentPct = Number(rtoShare)
        svc.prepaidRtoShipmentPct = Number(rtoShare)
      }
      // avgInvoiceValueInr = needed for the %-of-value legs of the COD charge AND the COD rails
      // (1.2/1.5/2% of invoice). Without it the engine can only apply the flat floors/ceiling.
      if (avgInvoiceValue) svc.avgInvoiceValueInr = Number(avgInvoiceValue)
    }
    // Reverse RVP params — now captured in Step 3.
    if (isReverse) {
      const rvp = cfg.rvp || {}
      if (rvp.minCost) svc.rvpMinCostInr = Number(rvp.minCost)
      if (rvp.pctOfForward) svc.rvpPctOfForward = Number(rvp.pctOfForward)
      if (rvp.qc) svc.qcChargeInr = Number(rvp.qc)
      if (!rvp.minCost && !rvp.pctOfForward) warnings.add('gen_rvp_params')
    }
    return svc
  })

  // Courier blacklist has no API field → carry it in capturedInputs (per product decision).
  // Same parent-name pivot as cpPreference above — cfg.blacklist entries are { parent } only.
  const courierBlacklist = {}
  services.forEach((k) => {
    const parents = ((svcConfig[k] || {}).blacklist || []).map((c) => c.parent).filter(Boolean)
    if (parents.length) courierBlacklist[serviceMeta(k).apiValue] = parents
  })

  // Hard Stop 4: explicit go-aheads on unserviceable cities — "recorded in the working
  // for the pricing team to see" (Decision Flow v7). Keyed by service → PINs approved.
  const serviceabilityGoAheads = {}
  services.forEach((k) => {
    const pins = (svcConfig[k] || {}).goAheads || []
    if (pins.length) serviceabilityGoAheads[serviceMeta(k).apiValue] = pins
  })

  const pickupLocations = pickups
    .filter((p) => p.pin)
    // Prefer the real zoneTag from the pincode master (already fetched into pinCache);
    // fall back to the local heuristic only when the pin hasn't been resolved.
    .map((p) => ({ pincode: p.pin, zoneTag: pinCache[p.pin]?.zoneTag || zoneTagForPin(p.pin), monthlyVolume: Number(p.vol) || 0 }))

  // rfiEntries: cross-multiply zone% × weight% per service. Merge cells that map to
  // the same (service, zone, slab) — e.g. two custom bands clamped to the same slab —
  // so we never send duplicate or invalid-slab entries.
  // BUG-07: when svcSplits is provided (Step-2 "split differs by service type"), each
  // service's OWN zone/weight split builds its entries — true per-service capture.
  // Without it, applying the one split to every service is an explicit user choice.
  if (!svcSplits) warnings.add('gen_rfi_crossmultiply')
  const rfiMap = {}
  services.forEach((k) => {
    const serviceType = serviceMeta(k).apiValue
    if (!serviceType) return
    const ws = svcSplits?.[k]?.wts || wtSplit
    const zs = svcSplits?.[k]?.zones || zoneSplit
    ws.forEach((w) => {
      const slab = bandToSlab(w.to)
      zs.forEach((z) => {
        const splitPct = ((Number(w.pct) || 0) * (Number(z.pct) || 0)) / 100
        if (splitPct > 0) {
          const zone = toApiZone(z.zone)
          const key = `${serviceType}|${zone}|${slab}`
          if (!rfiMap[key]) rfiMap[key] = { serviceType, zone, weightSlab: slab, splitPct: 0 }
          rfiMap[key].splitPct += splitPct
        }
      })
    })
  })
  const rfiEntries = Object.values(rfiMap).map((e) => ({ ...e, splitPct: Math.round(e.splitPct * 100) / 100 }))

  const payload = {
    rateCardMode: rateCardModeFor(cardType),
    services: apiServices,
    pickupLocations,
    rfiEntries,
    capturedInputs: {
      fscPct: 0,
      totalMonthlyVolume: totVol ? Number(totVol) : undefined,
      courierBlacklist: Object.keys(courierBlacklist).length ? courierBlacklist : undefined,
      serviceabilityGoAheads: Object.keys(serviceabilityGoAheads).length ? serviceabilityGoAheads : undefined,
      // Rate-card recipe v2 — collapsed client-PDF form + Step 2/3 resume. Counters,
      // system-margin and FSC clone capturedInputs backend-side; sent on /generate only.
      rateCardRecipe: buildRateCardRecipe({ services, svcConfig, wtTiers, svcSplits, courierWise: rateCardModeFor(cardType) === 'COURIER_WISE' }),
      // Freight-commercials scoping fields (Step 1) — no dedicated backend field, ride
      // in this schemaless map like courierBlacklist/standardCards above.
      registeredCompanyName: registeredCompany || undefined,
      enterpriseSize: enterpriseSize || undefined,
      brandTotalMonthlyVolume: brandTotalVol ? Number(brandTotalVol) : undefined,
      minWeightKg: minWt ? Number(minWt) : undefined,
      currentRvpPct: currentRvp ? Number(currentRvp) : undefined,
    },
  }
  return { payload, warnings: [...warnings] }
}

// ── Dashboard KPI tiles — computed from the REAL proposal list (no mock) ──────────
// Every dashboard already fetches the list; these are counts/sums/time-windows over it.
// SLA thresholds default to the documented config values (l1=30min, fh=8hr).
const KPI_OPEN = ['DRAFT', 'GENERATED', 'PENDING_L1', 'PENDING_FH', 'PUSHED_TO_PRICING_AWAITING_ORDER_DATA', 'RETURNED_TO_SL', 'RETURNED_TO_L1']
const KPI_APPROVED_PLUS = ['APPROVED', 'SENT', 'CONVERTED', 'LOST']
const _sameDay = (d) => { if (!d) return false; const x = new Date(d); const n = new Date(); return x.toDateString() === n.toDateString() }
const _sameMonth = (d) => { if (!d) return false; const x = new Date(d); const n = new Date(); return x.getMonth() === n.getMonth() && x.getFullYear() === n.getFullYear() }
const _minsSince = (d) => (d ? (Date.now() - new Date(d).getTime()) / 60000 : 0)
const _fmtMins = (m) => (m >= 60 ? `${(m / 60).toFixed(1)} hr` : `${Math.round(m)} min`)
// last 7 days, not calendar-week — matches _sameDay/_sameMonth's rolling style.
const _withinDays = (d, days) => (d ? Date.now() - new Date(d).getTime() <= days * 86400000 : false)
const _median = (nums) => {
  if (!nums.length) return null
  const s = [...nums].sort((a, b) => a - b)
  const mid = Math.floor(s.length / 2)
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2
}

export function computeKpis(rows = [], view = 'sl', config = {}) {
  const r = Array.isArray(rows) ? rows : []
  const n = (fn) => r.filter(fn).length
  const l1Sla = config?.l1SlaMinutes || 30
  const fhSlaH = config?.fhSlaHours || 8
  const tat = r.filter((x) => x.createdAt && x.updatedAt).map((x) => (new Date(x.updatedAt) - new Date(x.createdAt)) / 60000)
  const avgTat = tat.length ? _fmtMins(Math.round(tat.reduce((a, b) => a + b, 0) / tat.length)) : '—'

  if (view === 'sl') {
    const total = r.length || 1
    const approved = n((x) => KPI_APPROVED_PLUS.includes(x.status))
    const conv = r.filter((x) => x.status === 'CONVERTED' && _sameMonth(x.updatedAt))
    const mbr = conv.reduce((a, x) => a + (x.monthlyRevenuePotentialLacs || 0), 0)
    return [
      { label: 'My Open Commercials', value: String(n((x) => KPI_OPEN.includes(x.status))), delta: `${n((x) => x.status === 'PENDING_L1')} with L1` },
      { label: 'Avg Turnaround', value: avgTat, delta: 'created → last update' },
      { label: 'Approved', value: `${Math.round((approved / total) * 100)}%`, delta: `${approved} of ${r.length}` },
      { label: 'Converted This Month', value: String(conv.length), delta: mbr ? `₹${mbr.toFixed(1)} L MBR` : '—', dir: 'up' },
    ]
  }
  if (view === 'sl-queue') {
    const atL1 = n((x) => x.status === 'PENDING_L1')
    const atFh = n((x) => x.status === 'PENDING_FH')
    const returned = n((x) => x.status === 'RETURNED_TO_SL')
    return [
      { label: 'Awaiting Approval', value: String(atL1 + atFh), delta: `${atL1} at L1 · ${atFh} at FH` },
      { label: 'Returned to Me', value: String(returned), delta: returned ? 'needs rework' : 'nothing to rework', dir: returned ? 'dn' : 'up' },
      { label: 'Sent This Month', value: String(n((x) => ['SENT', 'CONVERTED', 'LOST'].includes(x.status) && _sameMonth(x.updatedAt))), delta: 'shared with client' },
      { label: 'Avg Time to Decision', value: avgTat, delta: 'created → last update' },
    ]
  }
  if (view === 'l1' || view === 'l1-queue') {
    const pending = r.filter((x) => x.status === 'PENDING_L1')
    const breach = pending.filter((x) => _minsSince(x.updatedAt) > l1Sla).length
    // "Decided" population = deals that have left L1's hands (STATUS_GROUPS.l1History) —
    // best-effort from list-level createdAt/updatedAt only (no per-transition timestamps
    // are exposed on the list API, so this is deal-lifetime, not strictly L1's own dwell
    // time — same class of approximation reasonFromStatus already documents).
    const decided = r.filter((x) => ['PENDING_FH', 'APPROVED', 'SENT', 'CONVERTED', 'LOST', 'RETURNED_TO_SL'].includes(x.status))
    const decidedTat = decided.filter((x) => x.createdAt && x.updatedAt).map((x) => (new Date(x.updatedAt) - new Date(x.createdAt)) / 60000)
    const medianReview = decidedTat.length ? _fmtMins(Math.round(_median(decidedTat))) : '—'
    // "Sent up" = L1's own action was a counter/escalation (status now PENDING_FH) — the
    // one L1-action signal still readable once a version exists (vs. approve, which is
    // final and needs no further FH decision). Route-3 rebuilds always land here too
    // (Decision Flow v7: "no L1-only sign-off"), which is consistent with "L1 sent it up."
    const counteredThisWeek = decided.filter((x) => x.status === 'PENDING_FH' && _withinDays(x.updatedAt, 7)).length
    // Once a deal moves past PENDING_FH (FH decides), the list can no longer tell whether
    // L1's original action was approve-as-submitted or counter/escalate — so this counts
    // "not currently sitting at PENDING_FH" as approved-as-submitted, which slightly
    // over-counts for old deals that were escalated and later closed. Documented, not hidden.
    const approvedAsSubmittedPct = decided.length ? Math.round((decided.filter((x) => x.status !== 'PENDING_FH').length / decided.length) * 100) : null

    if (view === 'l1-queue') {
      return [
        { label: 'Pending Review', value: String(pending.length), delta: breach ? `${breach} past ${l1Sla}-min SLA` : 'all within SLA', dir: breach ? 'dn' : 'up' },
        { label: 'Approved Today', value: String(n((x) => KPI_APPROVED_PLUS.includes(x.status) && _sameDay(x.updatedAt))), delta: 'incl. >20% band' },
        { label: 'Countered This Week', value: String(counteredThisWeek), delta: 'sent to Functional Head' },
        { label: 'Median Review Time', value: medianReview, delta: `target < ${l1Sla} min` },
      ]
    }
    return [
      { label: 'Pending My Review', value: String(pending.length), delta: breach ? `${breach} past ${l1Sla}-min SLA` : 'all within SLA', dir: breach ? 'dn' : 'up' },
      { label: 'Median Review Time', value: medianReview, delta: `target < ${l1Sla} min` },
      { label: 'Countered This Week', value: String(counteredThisWeek), delta: 'sent to Functional Head' },
      { label: 'Approved as Submitted', value: approvedAsSubmittedPct != null ? `${approvedAsSubmittedPct}%` : '—', delta: 'rest needed a change' },
    ]
  }
  // fh
  const pendingFh = r.filter((x) => x.status === 'PENDING_FH')
  const breachFh = pendingFh.filter((x) => _minsSince(x.updatedAt) > fhSlaH * 60).length
  return [
    { label: 'Pending My Sign-off', value: String(pendingFh.length), delta: breachFh ? `${breachFh} past ${fhSlaH} hr` : 'all within SLA', dir: breachFh ? 'dn' : 'up' },
    { label: 'Below-10% Rebuilds', value: String(n((x) => x.route === 3 && x.orderDataFileUri)), delta: 'order-level data attached' },
    { label: 'Approved Today', value: String(n((x) => KPI_APPROVED_PLUS.includes(x.status) && _sameDay(x.updatedAt))), delta: 'released' },
    { label: 'Declined (Lost)', value: String(n((x) => x.status === 'LOST')), delta: 'logged as lost' },
  ]
}

// ── Copy as template ─────────────────────────────────────────────────────────
// Reverse a stored version into wizard seed state for a NEW prospect. We seed only
// the cleanly-mappable commercial structure (card type, services, per-service
// mode/margin/SOW/charges) — never the prospect's brand/pickups/volumes/cities, and
// never the rate-grid (backend slabs don't map 1:1 to the wizard tier labels). All
// values are the proposal's REAL stored numbers — nothing fabricated.
const CARD_TYPE_REV = { BLENDED: 'blended', COURIER_WISE: 'cpwise', PRODUCT_SEGMENT: 'hb' }
const SERVICE_REV = { FWD_SURFACE: 'srf', FWD_AIR: 'air', SDD_NDD: 'sddndd', RVP: 'rvp' }
const INPUT_MODE_REV = { TARGET_MARGIN: 'margin', TARGET_RATE_CARD: 'ratecard' }
const str = (v) => (v != null && v !== '' ? String(v) : '')
// UI-only formula-type dropdown (Step 3 RVP) — infer it from which stored field(s) are
// present, so a resumed/copied draft's dropdown matches its actual saved values.
const rvpFormulaType = (minCost, pctOfForward) => {
  if (minCost && !pctOfForward) return 'flat'
  if (pctOfForward && !minCost) return 'pct'
  return 'higher'
}
export function versionToSeed(version = {}) {
  const cardType = CARD_TYPE_REV[version.rateCardMode] || 'blended'
  const services = []
  const svcCfgPatch = {}
  ;(version.services || []).forEach((s) => {
    const k = SERVICE_REV[s.serviceType] || s.serviceType   // legacy enum → key; rich code passes through
    if (!k || services.includes(k)) return
    services.push(k)
    svcCfgPatch[k] = {
      _seeded: true, // keep these template values; Step 3 still seeds cities from the new pickups
      mode: INPUT_MODE_REV[s.inputMode] || 'margin',
      margin: str(s.targetMarginPct),
      cod: { flat: str(s.codFlatInr), pct: str(s.codPctOfValue), rtoPct: str(s.rtoPctOfForward), prepaidRtoPct: str(s.prepaidRtoShipmentPct), codRtoPct: str(s.codRtoShipmentPct) },
      rvp: { minCost: str(s.rvpMinCostInr), pctOfForward: str(s.rvpPctOfForward), qc: str(s.qcChargeInr), formulaType: rvpFormulaType(s.rvpMinCostInr, s.rvpPctOfForward) },
      evidence: s.evidenceShared ? 'Yes' : 'No',
    }
  })
  return { cardType, services, svcCfgPatch }
}

// ── Resume draft — hydrate the full wizard from a saved deal (+ version if generated).
// Restores everything the backend persists; fields not sent to the backend (category,
// avg weight, COD/RTO share) can't be restored and stay blank for the SL to re-enter.
const RESUME_UI_ZONES = ['Local', 'Within State', 'Metro', 'ROI', 'Special']
const RESUME_API_TO_UI = { Local: 'Local', 'Within State': 'Within State', Metros: 'Metro', ROI: 'ROI', Special: 'Special' }
function rfiEntriesToSplits(rfi = []) {
  if (!rfi.length) return { zoneSplit: [], wtSplit: [] }
  const zAgg = {}; const wAgg = {}
  rfi.forEach((e) => {
    const uiZone = RESUME_API_TO_UI[e.zone] || e.zone
    zAgg[uiZone] = (zAgg[uiZone] || 0) + (Number(e.splitPct) || 0)
    const kg = parseFloat(String(e.weightSlab)) || 0
    wAgg[kg] = (wAgg[kg] || 0) + (Number(e.splitPct) || 0)
  })
  const zSum = RESUME_UI_ZONES.reduce((s, z) => s + (zAgg[z] || 0), 0) || 1
  const zoneSplit = RESUME_UI_ZONES.map((z) => ({ zone: z, pct: Math.round(((zAgg[z] || 0) / zSum) * 100) }))
  const slabs = Object.keys(wAgg).map(Number).sort((a, b) => a - b)
  const wSum = slabs.reduce((s, k) => s + wAgg[k], 0) || 1
  let prev = 0
  const wtSplit = slabs.map((k) => { const b = { from: String(prev), to: String(k), pct: Math.round((wAgg[k] / wSum) * 100) }; prev = k; return b })
  return { zoneSplit, wtSplit }
}
// FB-01 resume edge — per-service split from stored rfiEntries (grouped by serviceType), each
// shaped like svcSplits: { [uiKey]: { zones: [{zone,pct}], wts: [{from,to,pct}] } }.
function rfiEntriesToPerSvc(rfi = []) {
  const byType = {}
  rfi.forEach((e) => { (byType[e.serviceType] = byType[e.serviceType] || []).push(e) })
  const out = {}
  Object.entries(byType).forEach(([st, entries]) => {
    const { zoneSplit, wtSplit } = rfiEntriesToSplits(entries)
    out[SERVICE_REV[st] || st] = { zones: zoneSplit, wts: wtSplit }
  })
  return out
}
export function dealToResume(deal = {}, version = null) {
  const cardType = CARD_TYPE_REV[deal.cardType || version?.rateCardMode] || 'blended'
  const svcTypes = (deal.selectedServices && deal.selectedServices.length) ? deal.selectedServices : (version?.services || []).map((s) => s.serviceType)
  // legacy enum → its UI key; a rich serviceability code passes through as its own key.
  const services = svcTypes.map((st) => SERVICE_REV[st] || st).filter(Boolean)
  const svcCfgPatch = {}
  ;(version?.services || []).forEach((s) => {
    const k = SERVICE_REV[s.serviceType] || s.serviceType
    if (!k) return
    svcCfgPatch[k] = {
      _seeded: true,
      mode: INPUT_MODE_REV[s.inputMode] || 'margin',
      margin: str(s.targetMarginPct),
      cod: { flat: str(s.codFlatInr), pct: str(s.codPctOfValue), rtoPct: str(s.rtoPctOfForward), prepaidRtoPct: str(s.prepaidRtoShipmentPct), codRtoPct: str(s.codRtoShipmentPct) },
      rvp: { minCost: str(s.rvpMinCostInr), pctOfForward: str(s.rvpPctOfForward), qc: str(s.qcChargeInr), formulaType: rvpFormulaType(s.rvpMinCostInr, s.rvpPctOfForward) },
      evidence: s.evidenceShared ? 'Yes' : 'No',
      cities: (s.citiesInScope || []).map((v) => ({ value: v })),
      // cpPreference stores parent names — restore the priority list (also what the
      // courier-wise per-courier rate tabs key on).
      primary: (s.cpPreference || []).map((p) => ({ parent: p })),
    }
  })
  // Rate-card recipe v2 — restore the Step-2 structure (fresh flags included) and the
  // Step-3 compact rates exactly as the SL built them. Zone keys come back as API names;
  // map them to the UI's ('Metros' → 'Metro') and re-expand cfg.rates so the validator
  // and exports see the full grid immediately.
  const recipe = version?.capturedInputs?.rateCardRecipe || {}
  const uiZonesOf = (obj = {}) => {
    const out = {}
    Object.entries(obj || {}).forEach(([z, v]) => { if (v != null && String(v).trim() !== '') out[RESUME_API_TO_UI[z] || z] = String(v) })
    return out
  }
  const uiCompactOf = (c = {}) => ({
    first: uiZonesOf(c.first),
    firstEach: c.firstEach ? uiZonesOf(c.firstEach) : {},
    bands: Object.fromEntries(Object.entries(c.bands || {}).map(([i, b]) => [i, { ...(b?.base ? { base: uiZonesOf(b.base) } : {}), each: uiZonesOf(b?.each) }])),
  })
  let recipeTiers = null
  const recipeTiersByKey = {}
  Object.entries(recipe).forEach(([st, entry]) => {
    const k = SERVICE_REV[st] || st
    if (!k || !Array.isArray(entry?.structure) || !entry.structure.length) return
    const tiers = entry.structure.map((b, i) => (i === 0
      ? { size: Number(b.size) || 0.5, count: Math.max(1, Math.floor(Number(b.count) || 1)) }
      : { size: Number(b.size) || 1, count: Math.max(1, Math.floor(Number(b.count) || 1)), fresh: b.fresh !== false }))
    recipeTiersByKey[k] = tiers
    if (!recipeTiers) recipeTiers = tiers
    const patch = svcCfgPatch[k] || (svcCfgPatch[k] = { _seeded: true })
    if (entry.compactRates) {
      patch.compactRates = uiCompactOf(entry.compactRates)
      patch.rates = expandCompactRates(tiers, patch.compactRates, RESUME_UI_ZONES)
    }
    if (entry.courierRates) {
      patch.courierCompactRates = Object.fromEntries(Object.entries(entry.courierRates).map(([p, c]) => [p, uiCompactOf(c)]))
      const firstParent = (patch.primary || [])[0]?.parent
      const firstCompact = patch.courierCompactRates[firstParent] || Object.values(patch.courierCompactRates)[0]
      if (firstCompact) patch.rates = expandCompactRates(tiers, firstCompact, RESUME_UI_ZONES)
    }
  })
  const pickups = (version?.pickupLocations || []).map((p, i) => ({ id: i + 1, pin: p.pincode, vol: p.monthlyVolume != null ? String(p.monthlyVolume) : '' }))
  const totVol = version?.capturedInputs?.totalMonthlyVolume != null ? String(version.capturedInputs.totalMonthlyVolume) : ''
  const rfi = version?.rfiEntries || []
  const { zoneSplit, wtSplit } = rfiEntriesToSplits(rfi)
  // Restore the per-service split when the stored entries actually vary by service (else the
  // single global pair is right). Snapshot resumes already carry this; this fixes the
  // no-snapshot (generated-draft) path that used to collapse everything to one split (FB-01).
  const perSvc = rfiEntriesToPerSvc(rfi)
  const svcSigs = new Set(Object.values(perSvc).map((s) => JSON.stringify(s)))
  const perSvcSplit = Object.keys(perSvc).length > 1 && svcSigs.size > 1
  const svcSplits = perSvcSplit ? perSvc : {}
  // Per-service structures ride on svcSplits (same slot NewProposal's tiersFor reads).
  if (perSvcSplit) Object.keys(svcSplits).forEach((k) => { if (recipeTiersByKey[k]) svcSplits[k].tiers = recipeTiersByKey[k] })
  return {
    wtTiers: recipeTiers,
    proposalId: deal.id || deal.dealId,
    versionId: version?.id || deal.currentVersionId || null,
    cardType,
    services,
    svcCfgPatch,
    brand: deal.brandName || '',
    hubspot: deal.hubspotDealId || '',
    revPot: deal.monthlyRevenuePotentialLacs != null ? String(deal.monthlyRevenuePotentialLacs) : '',
    totVol,
    pickups,
    zoneSplit,
    wtSplit,
    perSvcSplit,
    svcSplits,
  }
}

// ── Counter diff (SL counter-review page) ─────────────────────────────────────
// What changed between two versions' rate cards, per service × weight slab ×
// zone. Nothing in the API diffs versions for us (confirmed: no delta field on
// any endpoint) — this walks services[].targetRateCards[].rows[].zoneRates on
// both versions and compares cell by cell.
// Wrinkle: if the OLDER version had no explicit rate card for a service (pure
// TARGET_MARGIN — the engine solved the rate itself), there's no card to diff
// against. Fall back to the implied "current" rate from that version's own
// computedLanes (first lane's revenueRate per cell — the same convention
// CounterEditorDialog's laneStruct already uses to seed "the current rate").
// Courier-wise rate cards carry one card PER COURIER (courierId = courier brand from the wizard, or
// the account code from the L1 counter editor). The diff, the SL's edits and the resubmit payload are
// keyed per CARD: a courier-less (blended) card keys as the bare serviceType — exactly as before — and a
// courier card as `${serviceType}::${courierId}`. Keying by service alone let each courier's cells
// overwrite the previous courier's, and the resubmit collapsed every courier back onto card #1.
const COUNTER_CARD_SEP = '::'
export const counterCardKey = (serviceType, courierId) => (courierId ? `${serviceType}${COUNTER_CARD_SEP}${courierId}` : serviceType)
export const parseCounterCardKey = (key = '') => {
  const i = key.indexOf(COUNTER_CARD_SEP)
  return i < 0 ? { serviceType: key, courierId: null } : { serviceType: key.slice(0, i), courierId: key.slice(i + COUNTER_CARD_SEP.length) }
}
// Does a lane belong to a card's courier? A card names the courier by account code or by brand.
const laneMatchesCourier = (lane, courierId) => !courierId
  || lane.courierId === courierId
  || String(lane.courierParent || '').toLowerCase() === String(courierId).toLowerCase()
// Readable courier name for a card: its brand when the card is keyed by an account code.
export const counterCourierName = (version = {}, courierId) => {
  if (!courierId) return null
  const lane = (version.computedLanes || []).find((l) => l.courierId === courierId)
  return lane?.courierParent || courierId
}
export function diffCounterRates(prevVersion = {}, newVersion = {}) {
  const impliedRate = (version, serviceType, weightSlab, zone, courierId) => {
    const lane = (version.computedLanes || []).find((l) => l.serviceType === serviceType && l.weightSlab === weightSlab
      && l.zone === zone && laneMatchesCourier(l, courierId))
    return lane?.revenueRate ?? null
  }
  const out = {}
  ;(newVersion.services || []).forEach((s) => {
    const st = s.serviceType
    const prevSvc = (prevVersion.services || []).find((p) => p.serviceType === st)
    // Previous version's explicit rate for this card: its own same-courier card, else the courier-less
    // card that priced every courier. Card ids can differ between versions (wizard keys by brand, the
    // counter editor by account), so a miss falls through to the priced lane — the rate that card
    // actually produced for THIS courier.
    const prevCards = prevSvc?.targetRateCards || []
    const prevRowsFor = (courierId) => {
      const own = courierId ? prevCards.find((c) => c.courierId && String(c.courierId).toLowerCase() === String(courierId).toLowerCase()) : null
      const card = own || prevCards.find((c) => !c.courierId)
      const byKey = {}
      ;(card?.rows || []).forEach((r) => { byKey[r.weightSlab] = r.zoneRates || {} })
      return byKey
    }
    ;(s.targetRateCards || []).forEach((card) => {
      const key = counterCardKey(st, card.courierId)
      const prevByKey = prevRowsFor(card.courierId)
      ;(card.rows || []).forEach((r) => {
        Object.entries(r.zoneRates || {}).forEach(([zone, after]) => {
          const hadExplicitBefore = prevByKey[r.weightSlab]?.[zone] != null
          const before = hadExplicitBefore ? prevByKey[r.weightSlab][zone] : impliedRate(prevVersion, st, r.weightSlab, zone, card.courierId)
          const changed = before == null || Number(before) !== Number(after)
          out[key] = out[key] || {}
          out[key][r.weightSlab] = out[key][r.weightSlab] || {}
          out[key][r.weightSlab][zone] = { before, after, changed }
        })
      })
    })
  })
  return out
}

// ── Counter review → POST /generate payload ───────────────────────────────────
// The SL's response to a counter isn't a fresh wizard pass — it's the SAME deal's
// latest (countered) version, with the reviewer's cells optionally adjusted. Built
// directly from that version (not through the wizard's tier-ladder svcConfig),
// since the version already carries the DISCRETE rates the engine wants.
// `edits` overrides specific cells: { [cardKey]: { [weightSlab]: { [zone]: number } } } — cardKey is
// counterCardKey(serviceType, courierId): the bare serviceType for a blended card.
// A service submits TARGET_RATE_CARD if the latest version has an explicit rate
// card for it (whether original or countered — either way there are real numbers
// to honour); otherwise it keeps its original TARGET_MARGIN ask untouched.
export function buildCounterReviewPayload(version = {}, edits = {}) {
  const services = (version.services || []).map((s) => {
    const st = s.serviceType
    const cards = (s.targetRateCards || []).filter((c) => (c.rows || []).length)
    const hasRateCard = cards.length > 0
    const svc = {
      serviceType: st,
      monthlyVolume: s.monthlyVolume || 0,
      sowPct: s.sowPct,
      citiesInScope: s.citiesInScope?.length ? s.citiesInScope : undefined,
      cpPreference: s.cpPreference?.length ? s.cpPreference : undefined,
      inputMode: hasRateCard ? 'TARGET_RATE_CARD' : (s.inputMode || 'TARGET_MARGIN'),
    }
    if (!hasRateCard) {
      svc.targetMarginPct = s.targetMarginPct
    } else {
      // Every card is carried forward with its own courierId — a courier-wise deal keeps one card per
      // courier (it used to be rebuilt from card #1 alone as a courier-less card, re-pricing every
      // courier at courier #1's rates).
      svc.targetRateCards = cards.map((card) => {
        const ed = edits[counterCardKey(st, card.courierId)] || {}
        return {
          courierId: card.courierId || null,
          rows: card.rows.map((r) => ({
            weightSlab: r.weightSlab,
            zoneRates: Object.fromEntries(Object.entries(r.zoneRates || {}).map(([zone, val]) => [zone, ed[r.weightSlab]?.[zone] ?? val])),
          })),
        }
      })
    }
    if (s.codFlatInr != null) svc.codFlatInr = s.codFlatInr
    if (s.codPctOfValue != null) svc.codPctOfValue = s.codPctOfValue
    if (s.rtoPctOfForward != null) svc.rtoPctOfForward = s.rtoPctOfForward
    if (s.codShipmentPct != null) svc.codShipmentPct = s.codShipmentPct
    if (s.rtoShipmentPct != null) svc.rtoShipmentPct = s.rtoShipmentPct
    // prepaidRtoShipmentPct/codRtoShipmentPct (D2C RTO Margin Breakout, §4) — were missing here:
    // this function rebuilds the /generate payload from the fetched version when the SL revises
    // a returned/countered deal, and it never carried these two forward even though the stored
    // version has them (buildGeneratePayload, the first-submission path, already handles them).
    if (s.prepaidRtoShipmentPct != null) svc.prepaidRtoShipmentPct = s.prepaidRtoShipmentPct
    if (s.codRtoShipmentPct != null) svc.codRtoShipmentPct = s.codRtoShipmentPct
    if (s.rtoCodShipmentPct != null) svc.rtoCodShipmentPct = s.rtoCodShipmentPct
    if (s.avgInvoiceValueInr != null) svc.avgInvoiceValueInr = s.avgInvoiceValueInr
    if (s.rvpMinCostInr != null) svc.rvpMinCostInr = s.rvpMinCostInr
    if (s.rvpPctOfForward != null) svc.rvpPctOfForward = s.rvpPctOfForward
    if (s.qcChargeInr != null) svc.qcChargeInr = s.qcChargeInr
    return svc
  })
  return {
    rateCardMode: version.rateCardMode || 'BLENDED',
    services,
    pickupLocations: version.pickupLocations || [],
    rfiEntries: version.rfiEntries || [],
    capturedInputs: {
      fscPct: version.capturedInputs?.fscPct ?? 0,
      totalMonthlyVolume: version.capturedInputs?.totalMonthlyVolume,
      courierBlacklist: version.capturedInputs?.courierBlacklist,
      serviceabilityGoAheads: version.capturedInputs?.serviceabilityGoAheads,
      // Recipe v2: this path POSTs /generate (unlike /counter, which clones
      // capturedInputs backend-side), so the recipe must ride along or the revised
      // version loses its collapsed PDF card. Safe verbatim — the backend reconciles
      // the recipe against the new lanes and falls back to the full table on any edit.
      rateCardRecipe: version.capturedInputs?.rateCardRecipe,
    },
  }
}

// ── Full read-only input summary (Review modal / DealReview page) ────────────
// Consolidates what the SL filled across Step 1 (prospect), Step 2 (RFI split)
// and Step 3 (commercial expectations) from what the backend actually persists
// on the version + proposal. `proposalRaw` is the untransformed GET /proposals/{id}
// object (brandName, hubspotDealId, monthlyRevenuePotentialLacs) — the version
// alone doesn't carry these. Margin fields pass straight through the API's own
// masking (null = masked for SL).
export function versionToSummary(version = {}, proposalRaw = {}) {
  const services = (version.services || []).map((s) => ({
    serviceType: s.serviceType,
    label: serviceLabel(s.serviceType),
    mode: INPUT_MODE_REV[s.inputMode] || s.inputMode || '—',
    targetMarginPct: s.targetMarginPct,
    marginPct: s.marginPct,
    sowPct: s.sowPct,
    citiesInScope: s.citiesInScope || [],
    cpPreference: s.cpPreference || [],
    courierBlacklist: version.capturedInputs?.courierBlacklist?.[s.serviceType] || [],
    goAheads: version.capturedInputs?.serviceabilityGoAheads?.[s.serviceType] || [],
    cod: {
      flat: s.codFlatInr, pct: s.codPctOfValue, rtoPct: s.rtoPctOfForward,
      codShipmentPct: s.codShipmentPct, rtoShipmentPct: s.rtoShipmentPct,
      prepaidRtoShipmentPct: s.prepaidRtoShipmentPct, codRtoShipmentPct: s.codRtoShipmentPct,
      rtoCodShipmentPct: s.rtoCodShipmentPct, avgInvoiceValueInr: s.avgInvoiceValueInr,
    },
    rvp: { minCost: s.rvpMinCostInr, pctOfForward: s.rvpPctOfForward, qc: s.qcChargeInr },
    evidenceShared: !!s.evidenceShared,
    targetRateCards: s.targetRateCards || [],
  }))
  return {
    brand: proposalRaw.brandName || version.dealId || '',
    hubspot: proposalRaw.hubspotDealId || '',
    card: cardLabel(version.rateCardMode, services.map((s) => s.serviceType)),
    totalMonthlyVolume: version.capturedInputs?.totalMonthlyVolume ?? null,
    monthlyRevenuePotentialLacs: proposalRaw.monthlyRevenuePotentialLacs ?? null,
    pickupLocations: version.pickupLocations || [],
    rfiEntries: version.rfiEntries || [],
    services,
  }
}

// Pincode → API zoneTag enum (Metro | Special | Standard). Local heuristic until
// the pincode master lookup (GET /masters/pincodes/{pin}) is wired per row.
const METRO_PINS = new Set(['110001', '400001', '560001', '600001', '700001'])
const SPECIAL_PREFIX = ['18', '19', '67', '68', '79']
export const zoneTagForPin = (pin = '') => {
  if (METRO_PINS.has(pin)) return 'Metro'
  if (SPECIAL_PREFIX.some((p) => pin.startsWith(p))) return 'Special'
  return 'Standard'
}

// ── GAP NOTES ────────────────────────────────────────────────────────────────
// Precise, user-facing explanations of every UI element the API can't (yet) take.
// Rendered via <GapNote id="..."/> (components/common.js). Keep the text exact.
export const NOTES = {
  input_standard: {
    title: 'Standard (by size) — inputMode not in the API',
    body: 'The API inputMode accepts only TARGET_MARGIN and TARGET_RATE_CARD. The UI\'s "Standard (by size)" mode has no API equivalent and is not sent. Backend must confirm whether a standard/size-based mode is supported.',
  },
  zone_metro: {
    title: 'Zone label normalised',
    body: 'The UI label "Metro" is sent to the API as "Metros" (the API\'s exact enum). No action needed — flagged for transparency.',
  },
  input_mode_standard_short: {
    title: '"Standard" mode is not sent to the API',
    body: 'Only Target Margin and Target Rate Card are transmitted. See note above.',
  },
  kpi_placeholder: {
    title: 'KPI tiles are placeholders',
    body: 'There is no aggregation endpoint in the 25-API set for these summary counts. The tiles below show representative values until a stats API is provided. The tables underneath are live.',
  },
  heatmap_aggregate: {
    title: 'Rate-card heat-map aggregates lanes',
    body: 'Each cell is the volume-weighted average margin of all computedLanes in that weight × zone (across services/couriers). Per-lane detail is in the working-file exports. Cells show "—" where no lane exists or margin is masked.',
  },
  audit_from_chain: {
    title: 'Audit shows this version\'s approval chain',
    body: 'The trail is built from the version\'s approvalChain. A full cross-version, cross-actor audit (with system re-route entries) lives in the proposal\'s events[] — open the proposal for that. "What changed" per version is not in the API.',
  },
  counter_rates: {
    title: 'Counter editor does not yet capture revised rates',
    body: 'POST /counter expects sections[].rateCards[].rows[].zoneRates, but the in-UI editor only marks each section "reviewed" — it has no rate-cell inputs. The counter is submitted with the reviewed section structure but WITHOUT revised rate numbers. The rate-grid inputs must be built before counters are real.',
  },
  counter_upload: {
    title: 'Pack file storage is Phase 2 (per backend guide v2.1)',
    body: 'Attach all six files to unlock submit. Submitting routes the counter for real (POST /counter — the deal moves to the chosen destination); persisting the attached files to S3 is deferred to Phase 2 by the backend, so the files themselves stay on this device for now. For revised rate numbers, use "Create Counter Proposal in UI".',
  },
  review_no_deal: {
    title: 'Open a deal from the queue',
    body: 'This review screen needs a version to load. Open a deal from the Approval Queue (the Review button) — direct navigation/refresh has no version context.',
  },
  gen_fwd_cod: {
    title: 'Forward COD / RTO charge inputs not captured',
    body: 'Step 3 has no fields for this service’s COD flat fee, COD % of value, or RTO % of forward, so all three are omitted and the pricing engine falls back to its defaults. codShipmentPct/rtoShipmentPct (what fraction of shipments are COD/RTO) apply from Step 1’s deal-level COD/RTO share if you filled those. Still genuinely missing: avgInvoiceValueInr (needed for the COD-%-of-value formula) has no Step 3 field at all yet.',
  },
  gen_target_ratecard: {
    title: 'Target Rate Card sends an empty grid',
    body: 'TARGET_RATE_CARD mode requires targetRateCards[].rows[].zoneRates (a full weight × zone grid), but Step 3 only captures a single "client target rate" number. targetRateCards is sent empty. A full rate-card input grid must be built for this mode.',
  },
  gen_rfi_crossmultiply: {
    title: 'One RFI split applied to all services (explicit choice)',
    body: 'Step 2 now supports per-service splits ("Split differs by service type"). This proposal was generated with that toggle OFF, so the one zone/weight split was deliberately applied to every service. Regenerate with the toggle ON to capture different splits per service.',
  },
  config_courier_guardrails: {
    title: 'Courier guardrails: persisted; engine enforcement pending',
    body: 'RESOLVED (config): courierGuardrails now persist in D2cProposalConfig and the fh/rules toggles read/write them via GET/PUT /config. STILL OPEN (engine): breach-based routing off the named guardrails (breach → L1 even above 20%) is not yet enforced by the pricing engine — with the backend team.',
  },
  config_library: {
    title: 'Rate-card library is not in the config API',
    body: 'D2cProposalConfig has no rate-card library list. This table is representative and read-only. Backend must expose a library endpoint to make it live.',
  },
  config_users: {
    title: 'Routing comes from slRoutingMap (IDs, no names)',
    body: 'GET /config.slRoutingMap maps SL userId → { l1UserId, fhUserId } — IDs only, no names, and no per-user HubSpot/notification rules. This table stays representative; live routing edits need a richer config + a user-name lookup.',
  },
  masters_no_import: {
    title: 'Only pincode/zone import has an API',
    body: 'POST /admin/masters/pincodes/import handles the Zone Mapping / pincode master (.xlsx). The other masters (Supply Commercials, Drop-PIN Serviceability, CP↔Service mapping, Ecom Block) and the CP Capability matrix have NO import/read endpoint in the 25-API set — those uploads are not wired.',
  },
  masters_blacklist: {
    title: 'No blacklist API',
    body: 'There is no pincode-blacklist endpoint. The pincode import (POST /admin/masters/pincodes/import) upserts the Zone Mapping master only. Blacklist upload is not wired — backend must confirm.',
  },
  transfer_owner_id: {
    title: 'New owner is entered as a user ID',
    body: 'PUT /admin/proposals/{id}/transfer takes a newOwnerId (user ID). There is no user-list endpoint in the 25-API set to pick from, so the ID is typed manually. A user-picker needs a users API.',
  },
}
